import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";
import {
  ApiError,
  createAdminUser,
  deleteAdminUser,
  getAdminTeams,
  getAdminUserDetail,
  getAdminUsers,
  grantAdminTeamMembership,
  updateAdminUser,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableHeaderRow,
  DataTablePagination,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { unscopedKey } from "@/lib/queryClient";
import type { AdminTeamRow, AdminUser } from "@/lib/types";
import { AdminIconButton } from "../components/AdminIconButton";

/**
 * M8c G1–G6 — People > Accounts.
 *
 * Operator accounts management for global admins, embedded in
 * `/admin/people` so account identity and team assignment controls
 * live in one greenfield People console. Renders a list of every
 * signed-up user across all tenants with inline Edit / Toggle
 * (disable/enable) / Delete actions, an Add User modal, and a detail
 * drawer (G3) that shows the full 360 view (profile + cross-tenant
 * memberships + linked players) via the `/api/users/{id}` endpoint.
 *
 * G6 — the per-row "View teams" expander is a lightweight
 * collapsed alternative to opening the full detail drawer; it
 * fetches the SAME `/api/users/{id}` payload (so the drawer's
 * memberships and the expander's memberships are coherent) and
 * caches it for the page's lifetime.
 */

const PLATFORM_ROLES = ["admin", "uploader", "viewer"] as const;
const TEAM_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10 / page" },
  { value: "25", label: "25 / page" },
  { value: "50", label: "50 / page" },
];

/** Shared TanStack Query options for the user-detail payload. Both
 * the G3 detail drawer AND the G6 inline expander subscribe to the
 * same key + queryFn + staleTime — without this, the drawer
 * defaulted to `staleTime: 0` and refetched immediately after the
 * expander already had a fresh result, breaking the
 * "one-fetch-shared-by-both-surfaces" contract. Re-review P2 fix. */
function useAdminUserDetail(userId: string) {
  return useQuery({
    queryKey: unscopedKey("admin-user-detail", { userId }),
    queryFn: () => getAdminUserDetail(userId),
    staleTime: 5 * 60_000,
  });
}

export function AdminUsersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: unscopedKey("admin-users"),
    queryFn: getAdminUsers,
  });

  const users = usersQuery.data ?? [];
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [accountStatusFilter, setAccountStatusFilter] = useState("all");
  const [authMigrationFilter, setAuthMigrationFilter] = useState("all");
  const [pageSize, setPageSize] = useState("10");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [detailTarget, setDetailTarget] = useState<AdminUser | null>(null);
  const [assignTarget, setAssignTarget] = useState<AdminUser | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        const roles = u.roles ?? splitRoleString(u.role);
        const matchesText =
          !q ||
          u.username.toLowerCase().includes(q) ||
          (u.display_name ?? "").toLowerCase().includes(q);
        const matchesRole =
          roleFilter === "all" || roles.includes(roleFilter);
        const matchesStatus =
          accountStatusFilter === "all" ||
          (accountStatusFilter === "active" ? u.enabled : !u.enabled);
        const kratosLinked = !!u.auth_migration?.kratos_linked;
        const matchesAuthMigration =
          authMigrationFilter === "all" ||
          (authMigrationFilter === "kratos_linked" ? kratosLinked : !kratosLinked);
        return matchesText && matchesRole && matchesStatus && matchesAuthMigration;
      })
      .sort(compareAdminUsers);
  }, [users, search, roleFilter, accountStatusFilter, authMigrationFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, accountStatusFilter, authMigrationFilter, pageSize]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSizeNumber));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSizeNumber;
    return filtered.slice(start, start + pageSizeNumber);
  }, [filtered, pageSizeNumber, safePage]);
  const firstVisible = filtered.length === 0 ? 0 : (safePage - 1) * pageSizeNumber + 1;
  const lastVisible = Math.min(filtered.length, safePage * pageSizeNumber);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-users") });
  const invalidateUserDetail = (userId: string) =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-user-detail", { userId }),
    });

  return (
    <div data-testid="admin-users" className="flex flex-col gap-3">
      <header
        className={
          embedded
            ? "flex items-center justify-end gap-2"
            : "flex items-start justify-between flex-wrap gap-2"
        }
      >
        {!embedded ? (
          <div className="min-w-0">
          <h2 className="text-base font-display uppercase tracking-wider text-ink">
            Users
          </h2>
        </div>
        ) : null}
        <div className="flex items-center gap-2">
          <span className="text-[0.6rem] font-mono text-ink-muted">
            {usersQuery.isFetching ? "Refreshing…" : `${filtered.length} of ${users.length}`}
          </span>
          <AdminIconButton
            icon={Plus}
            label="Add user"
            size="md"
            tone="accent"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-users-new"
          />
        </div>
      </header>

      <section
        aria-label="Account filters"
        className="grid gap-2 rounded border border-rule bg-surface p-2 shadow-sm sm:grid-cols-[minmax(14rem,1fr)_11rem_11rem_11rem_9rem]"
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or username…"
          data-testid="admin-users-search"
          className="h-9 rounded border border-rule bg-field px-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <TkticSelect
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: "all", label: "All roles" },
            ...PLATFORM_ROLES.map((role) => ({ value: role, label: role })),
          ]}
          ariaLabel="Role filter"
          size="sm"
          data-testid="admin-users-role-filter"
        />
        <TkticSelect
          value={accountStatusFilter}
          onChange={setAccountStatusFilter}
          options={[
            { value: "all", label: "All status" },
            { value: "active", label: "Active" },
            { value: "disabled", label: "Disabled" },
          ]}
          ariaLabel="Account status filter"
          size="sm"
          data-testid="admin-users-status-filter"
        />
        <TkticSelect
          value={authMigrationFilter}
          onChange={setAuthMigrationFilter}
          options={[
            { value: "all", label: "All auth" },
            { value: "kratos_linked", label: "Secure sign-in linked" },
            { value: "needs_kratos", label: "Needs secure sign-in" },
          ]}
          ariaLabel="Auth migration filter"
          size="sm"
          data-testid="admin-users-auth-filter"
        />
        <TkticSelect
          value={pageSize}
          onChange={setPageSize}
          options={PAGE_SIZE_OPTIONS}
          ariaLabel="Rows per page"
          size="sm"
          data-testid="admin-users-page-size"
        />
      </section>

      {usersQuery.error ? (
        <p
          data-testid="admin-users-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load users.
        </p>
      ) : null}

      <DataTableShell>
        {filtered.length === 0 ? (
          <p
            data-testid="admin-users-empty"
            className="text-xs font-mono text-ink-muted p-3"
          >
            No users.
          </p>
        ) : (
          <>
          {/* #211 — promoted from `md:block` to `lg:block` so the
              920px-min-width table doesn't horizontally overflow at the
              md breakpoint (768px). Tablet now gets the same card layout
              as mobile, which keeps row actions reachable without a
              horizontal scroll. */}
          <DataTableScroll className="hidden lg:block">
            <DataTable
              data-testid="admin-users-table"
              className="min-w-[920px]"
            >
              <DataTableHead>
                <DataTableHeaderRow>
                  <DataTableHeadCell>Person</DataTableHeadCell>
                  <DataTableHeadCell>Role</DataTableHeadCell>
                  <DataTableHeadCell>Team status</DataTableHeadCell>
                  <DataTableHeadCell>Account</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </DataTableHeaderRow>
              </DataTableHead>
              <DataTableBody>
                {pageRows.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    expanded={expandedUserId === u.id}
                    onToggleExpand={() =>
                      setExpandedUserId((id) => (id === u.id ? null : u.id))
                    }
                    onOpenDetail={() => setDetailTarget(u)}
                    onAssign={() => setAssignTarget(u)}
                    onEdit={() => setEditTarget(u)}
                    onDelete={() => setDeleteTarget(u)}
                  />
                ))}
              </DataTableBody>
            </DataTable>
          </DataTableScroll>
          {/* #211 — matches the table breakpoint above; cards now
              cover all viewports < lg (1024px), eliminating the
              tablet-width horizontal scroll. */}
          <div className="divide-y divide-rule/60 lg:hidden">
            {pageRows.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                expanded={expandedUserId === u.id}
                onToggleExpand={() =>
                  setExpandedUserId((id) => (id === u.id ? null : u.id))
                }
                onOpenDetail={() => setDetailTarget(u)}
                onAssign={() => setAssignTarget(u)}
                onEdit={() => setEditTarget(u)}
                onDelete={() => setDeleteTarget(u)}
              />
            ))}
          </div>
          </>
        )}
      </DataTableShell>

      {filtered.length > 0 ? (
        <DataTablePagination
          first={firstVisible}
          last={lastVisible}
          total={filtered.length}
          page={safePage}
          totalPages={totalPages}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          summaryTestId="admin-users-pagination-summary"
          previousTestId="admin-users-prev-page"
          indicatorTestId="admin-users-page-indicator"
          nextTestId="admin-users-next-page"
        />
      ) : null}

      {createOpen ? (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {editTarget ? (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void invalidate();
          }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteUserConfirm
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            void invalidate();
          }}
        />
      ) : null}

      {detailTarget ? (
        <UserDetailModal
          user={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      ) : null}

      {assignTarget ? (
        <AssignUserToTeamModal
          user={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            const userId = assignTarget.id;
            setAssignTarget(null);
            setExpandedUserId(userId);
            void invalidate();
            void invalidateUserDetail(userId);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// G1 — list row + G6 inline expander
// ---------------------------------------------------------------------------

function UserRow({
  user,
  expanded,
  onToggleExpand,
  onOpenDetail,
  onAssign,
  onEdit,
  onDelete,
}: {
  user: AdminUser;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenDetail: () => void;
  onAssign: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const roles = user.roles ?? splitRoleString(user.role);
  return (
    <>
      <DataTableRow
        data-testid="admin-users-row"
        data-user-id={user.id}
        data-enabled={user.enabled ? "true" : "false"}
        className="align-top transition-colors hover:bg-elevated/60"
      >
        <DataTableCell className="align-top">
          <div className="text-ink truncate">
            {user.display_name || user.username}{" "}
            <span className="text-ink-muted">@{user.username}</span>
          </div>
          <div className="mt-0.5 font-mono text-[0.6rem] text-ink-muted">
            {user.id}
          </div>
        </DataTableCell>
        <DataTableCell className="align-top">
          <div className="flex flex-wrap items-center gap-1">
            {roles.map((r) => (
              <RolePill key={r} role={r} />
            ))}
            {/* Re-review P2: derive the Global-admin indicator from
             * the parsed `roles` array. `/api/users` returns
             * `_scrub_user(db_row)` which has `role` (comma-joined
             * string) but NOT a computed `is_global_admin` flag —
             * that lives only on `/api/me`. Reading `user.is_global_admin`
             * here meant the pill never rendered. Matches legacy
             * `renderUsersList` which derived the indicator from the
             * comma-joined role string. */}
            {roles.includes("admin") ? (
              <span
                data-testid="admin-users-global-admin-pill"
                className="rounded border border-accent/40 bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent"
              >
                Global admin
              </span>
            ) : null}
          </div>
        </DataTableCell>
        <DataTableCell className="align-top">
          <UserTeamStatus userId={user.id} />
        </DataTableCell>
        <DataTableCell className="align-top">
          <div className="flex flex-wrap items-center gap-1">
            <span
              data-testid="admin-users-status-pill"
              data-enabled={user.enabled ? "true" : "false"}
              className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${
                user.enabled
                  ? "border-good/40 text-good"
                  : "border-rule text-ink-muted"
              }`}
            >
              {user.enabled ? "Active" : "Disabled"}
            </span>
            <AuthMigrationPill user={user} />
          </div>
        </DataTableCell>
        <DataTableCell align="right" className="align-top">
          <div className="flex justify-end gap-1">
          <AdminIconButton
            icon={expanded ? ChevronUp : ChevronDown}
            label={expanded ? "Hide teams" : "View teams"}
            onClick={onToggleExpand}
            aria-expanded={expanded}
            data-testid="admin-users-expand"
          />
          <AdminIconButton
            icon={UserPlus}
            label="Assign to team"
            onClick={onAssign}
            data-testid="admin-users-assign"
          />
          <AdminIconButton
            icon={Eye}
            label="View user details"
            onClick={onOpenDetail}
            data-testid="admin-users-detail"
          />
          <AdminIconButton
            icon={Pencil}
            label="Edit user"
            onClick={onEdit}
            data-testid="admin-users-edit"
          />
          <AdminIconButton
            icon={Trash2}
            label="Delete user"
            onClick={onDelete}
            data-testid="admin-users-delete"
            tone="destructive"
          />
          </div>
        </DataTableCell>
      </DataTableRow>
      {expanded ? (
        <DataTableRow interactive={false} className="bg-elevated">
          <DataTableCell colSpan={5} className="align-top">
            <UserMembershipsExpander userId={user.id} />
          </DataTableCell>
        </DataTableRow>
      ) : null}
    </>
  );
}

function UserCard({
  user,
  expanded,
  onToggleExpand,
  onOpenDetail,
  onAssign,
  onEdit,
  onDelete,
}: {
  user: AdminUser;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenDetail: () => void;
  onAssign: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const roles = user.roles ?? splitRoleString(user.role);
  return (
    <article
      data-testid="admin-users-mobile-card"
      data-user-id={user.id}
      data-enabled={user.enabled ? "true" : "false"}
      className="flex flex-col gap-3 p-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm text-ink">
            {user.display_name || user.username}
          </h3>
          <p className="truncate text-xs text-ink-muted">@{user.username}</p>
          <p className="mt-1 truncate font-mono text-[0.6rem] text-ink-muted">
            {user.id}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <span
            data-testid="admin-users-status-pill"
            data-enabled={user.enabled ? "true" : "false"}
            className={`rounded border bg-surface px-2 py-1 text-[0.55rem] font-display uppercase tracking-wider ${
              user.enabled
                ? "border-good/40 text-good"
                : "border-rule text-ink-muted"
            }`}
          >
            {user.enabled ? "Active" : "Disabled"}
          </span>
          <AuthMigrationPill user={user} />
        </div>
      </header>

      <div className="grid gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Role
          </span>
          <div className="flex flex-wrap items-center gap-1">
            {roles.map((r) => (
              <RolePill key={r} role={r} />
            ))}
            {roles.includes("admin") ? (
              <span
                data-testid="admin-users-global-admin-pill"
                className="rounded border border-accent/40 bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent"
              >
                Global admin
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Team status
          </span>
          <UserTeamStatus userId={user.id} />
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-1.5">
        <AdminIconButton
          icon={expanded ? ChevronUp : ChevronDown}
          label={expanded ? "Hide teams" : "View teams"}
          onClick={onToggleExpand}
          aria-expanded={expanded}
          size="md"
          data-testid="admin-users-mobile-expand"
        />
        <AdminIconButton
          icon={UserPlus}
          label="Assign to team"
          onClick={onAssign}
          size="md"
          data-testid="admin-users-mobile-assign"
        />
        <AdminIconButton
          icon={Eye}
          label="View user details"
          onClick={onOpenDetail}
          size="md"
          data-testid="admin-users-mobile-detail"
        />
        <AdminIconButton
          icon={Pencil}
          label="Edit user"
          onClick={onEdit}
          size="md"
          data-testid="admin-users-mobile-edit"
        />
        <AdminIconButton
          icon={Trash2}
          label="Delete user"
          onClick={onDelete}
          size="md"
          data-testid="admin-users-mobile-delete"
          tone="destructive"
        />
      </div>

      {expanded ? (
        <div className="rounded border border-rule bg-elevated pt-2">
          <UserMembershipsExpander
            userId={user.id}
            testIdPrefix="admin-users-mobile"
          />
        </div>
      ) : null}
    </article>
  );
}

function UserTeamStatus({ userId }: { userId: string }) {
  const query = useAdminUserDetail(userId);

  if (query.isLoading) {
    return (
      <div
        data-testid="admin-users-team-status"
        className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-mono text-ink-muted"
      >
        Checking teams…
      </div>
    );
  }
  if (query.error) {
    return (
      <div
        data-testid="admin-users-team-status"
        className="rounded border border-destructive/40 bg-elevated px-2 py-1 text-[0.6rem] font-mono text-destructive"
      >
        Team status unavailable
      </div>
    );
  }
  const memberships = query.data?.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div
        data-testid="admin-users-team-status"
        data-status="none"
        className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
      >
        No team
      </div>
    );
  }
  if (memberships.length === 1) {
    const membership = memberships[0];
    return (
      <div
        data-testid="admin-users-team-status"
        data-status="assigned"
        className="min-w-0 rounded border border-good/40 bg-elevated px-2 py-1"
      >
        <div className="truncate text-[0.65rem] text-ink">
          {membership.team_name}
        </div>
        <div className="truncate text-[0.55rem] font-display uppercase tracking-wider text-good">
          {membership.role.replace("_", " ")}
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="admin-users-team-status"
      data-status="multiple"
      className="min-w-0 rounded border border-accent/40 bg-elevated px-2 py-1"
    >
      <div className="text-[0.65rem] text-ink">{memberships.length} teams</div>
      <div className="truncate text-[0.55rem] font-mono text-ink-muted">
        {memberships
          .slice(0, 2)
          .map((m) => m.team_name)
          .join(", ")}
      </div>
    </div>
  );
}

function RolePill({ role }: { role: string }) {
  const tone =
    role === "admin"
      ? "border-accent text-accent"
      : role === "uploader"
        ? "border-good/40 text-good"
        : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-users-role-pill"
      data-role={role}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {role}
    </span>
  );
}

function AuthMigrationPill({ user }: { user: AdminUser }) {
  const migration = user.auth_migration;
  const linked = !!migration?.kratos_linked;
  const verified = !!migration?.kratos_email_verified;
  const tone = linked
    ? verified
      ? "border-good/40 text-good"
      : "border-accent/40 text-accent"
    : "border-rule text-ink-muted";
  const label = linked
    ? verified
      ? "Secure sign-in"
      : "Sign-in unverified"
    : "Needs secure sign-in";
  return (
    <span
      data-testid="admin-users-auth-migration-pill"
      data-kratos-linked={linked ? "true" : "false"}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

// G6 — Memberships expander. Reuses the `/api/users/{id}` endpoint
// (same payload as the detail drawer) so the two surfaces stay
// coherent. Cached by user id via the shared `useAdminUserDetail`
// hook — opening then re-opening doesn't refetch, AND opening the
// drawer after the expander reuses the same fresh cache (re-review
// P2 fix: both observers must use the same staleTime).
function UserMembershipsExpander({
  userId,
  testIdPrefix = "admin-users",
}: {
  userId: string;
  testIdPrefix?: "admin-users" | "admin-users-mobile";
}) {
  const query = useAdminUserDetail(userId);

  if (query.isLoading) {
    return (
      <div className="px-3 pb-2 text-[0.6rem] font-mono text-ink-muted">
        Loading teams…
      </div>
    );
  }
  if (query.error) {
    return (
      <div
        data-testid={`${testIdPrefix}-memberships-error`}
        className="px-3 pb-2 text-[0.6rem] font-mono text-destructive"
      >
        Failed to load memberships.
      </div>
    );
  }
  const memberships = query.data?.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div
        data-testid={`${testIdPrefix}-memberships-empty`}
        className="px-3 pb-2 text-[0.6rem] font-mono text-ink-muted"
      >
        No team memberships.
      </div>
    );
  }
  return (
    <ul
      data-testid={`${testIdPrefix}-memberships`}
      className="px-3 pb-2 flex flex-col gap-1"
    >
      {memberships.map((m) => (
        <li
          key={m.id}
          data-testid="admin-users-membership-row"
          data-team-id={m.team_id}
          className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1 text-[0.65rem]"
        >
          <span className="text-ink truncate">
            {m.team_name}{" "}
            <span className="font-mono text-ink-muted">· {m.team_slug}</span>
          </span>
          <span
            data-role={m.role}
            className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
          >
            {m.role.replace("_", " ")}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// G2 — Add user modal
// ---------------------------------------------------------------------------

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>(["viewer"]);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createAdminUser({
        username,
        password,
        role: roles.length > 0 ? roles.join(",") : "viewer",
        display_name: displayName,
        assign_default_team: false,
      }),
    onSuccess: () => {
      toast.success("User created");
      onCreated();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't create user", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Add user"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Username
            </span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={2}
              maxLength={50}
              data-testid="admin-users-new-username"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Display name
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              data-testid="admin-users-new-display-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              data-testid="admin-users-new-password"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Roles (at least one)
            </span>
            <div className="flex flex-wrap gap-1">
              {PLATFORM_ROLES.map((r) => {
                const checked = roles.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRoles((rs) =>
                        checked
                          ? rs.filter((x) => x !== r)
                          : Array.from(new Set([...rs, r])),
                      );
                    }}
                    data-testid={`admin-users-new-role-${r}`}
                    data-active={checked ? "true" : undefined}
                    className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          {error ? (
            <p
              data-testid="admin-users-new-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Creating…" : "Create user"}
      onConfirm={() => {
        setError(null);
        if (!username.trim() || username.length < 2) {
          setError("Username must be at least 2 characters.");
          return;
        }
        if (password.length < 8) {
          setError("Password must be at least 8 characters.");
          return;
        }
        if (roles.length === 0) {
          setError("Pick at least one role.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-new-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// G4 — Edit user modal
// ---------------------------------------------------------------------------

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const initialRoles = user.roles ?? splitRoleString(user.role);
  const [displayName, setDisplayName] = useState(user.display_name);
  const [roles, setRoles] = useState<string[]>(initialRoles);
  const [enabled, setEnabled] = useState(user.enabled);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const dirty =
    displayName !== user.display_name ||
    enabled !== user.enabled ||
    roles.join(",") !== initialRoles.join(",") ||
    password.length > 0;

  const mut = useMutation({
    mutationFn: () =>
      updateAdminUser(user.id, {
        display_name:
          displayName !== user.display_name ? displayName : undefined,
        enabled: enabled !== user.enabled ? enabled : undefined,
        role:
          roles.join(",") !== initialRoles.join(",") ? roles.join(",") : undefined,
        password: password.length >= 8 ? password : undefined,
      }),
    onSuccess: () => {
      toast.success("User updated");
      onSaved();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string" && err.detail === "last_admin"
            ? "Cannot demote or disable the only enabled global admin."
            : typeof err.detail === "string"
              ? err.detail
              : "Last-admin protection: cannot demote the only enabled global admin.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't update user", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Edit user"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Username (read-only)
            </span>
            <input
              type="text"
              value={user.username}
              disabled
              data-testid="admin-users-edit-username"
              className="rounded border border-rule bg-surface/50 px-2 py-1.5 text-xs text-ink-muted font-mono cursor-not-allowed"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Display name
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              data-testid="admin-users-edit-display-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Roles
            </span>
            <div className="flex flex-wrap gap-1">
              {PLATFORM_ROLES.map((r) => {
                const checked = roles.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRoles((rs) =>
                        checked
                          ? rs.filter((x) => x !== r)
                          : Array.from(new Set([...rs, r])),
                      );
                    }}
                    data-testid={`admin-users-edit-role-${r}`}
                    data-active={checked ? "true" : undefined}
                    className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            data-testid="admin-users-edit-enabled"
            data-active={enabled ? "true" : undefined}
            className="self-start rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
          >
            {enabled ? "Account enabled" : "Account disabled"}
          </button>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Password reset (optional)
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              placeholder="Leave blank to keep current"
              data-testid="admin-users-edit-password"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <span className="text-[0.55rem] font-mono text-ink-muted">
              Setting a new password revokes the user's other sessions on next
              login.
            </span>
          </label>
          {error ? (
            <p
              data-testid="admin-users-edit-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Saving…" : "Save changes"}
      onConfirm={() => {
        setError(null);
        if (!dirty) {
          onClose();
          return;
        }
        if (password.length > 0 && password.length < 8) {
          setError("Password must be at least 8 characters.");
          return;
        }
        if (roles.length === 0) {
          setError("Pick at least one role.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-edit-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// G5 — Delete user confirm
// ---------------------------------------------------------------------------

function DeleteUserConfirm({
  user,
  onClose,
  onDeleted,
}: {
  user: AdminUser;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const kratosLinked = !!user.auth_migration?.kratos_linked;
  const confirmed = confirmText.trim() === user.username;
  const mut = useMutation({
    mutationFn: () =>
      deleteAdminUser(user.id, { confirm_username: confirmText.trim() }),
    onSuccess: () => {
      toast.success("User deleted everywhere");
      onDeleted();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string" && err.detail === "last_admin"
            ? "Cannot delete the only enabled global admin."
            : typeof err.detail === "string"
              ? err.detail
              : "Last-admin protection: cannot delete the only enabled global admin.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't delete user", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Delete account everywhere?"
      message={
        <div className="flex flex-col gap-3">
          <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
            This permanently removes the account from Tktic
            {kratosLinked ? " and its linked secure sign-in identity" : ""}.
            This cannot be undone.
          </p>
          <p>
            <strong>{user.display_name || user.username}</strong> (@
            {user.username}) will lose team memberships, player links, app
            sessions, password recovery tokens, and sign-in access.
          </p>
          {kratosLinked ? (
            <p>
              The linked secure sign-in identity (and its passkeys and
              sessions) will be deleted before Tktic removes the local
              account. If that cleanup fails, Tktic will keep the local
              account so you can retry safely.
            </p>
          ) : (
            <p>
              No linked secure sign-in identity is recorded for this account;
              this delete will only remove the local Tktic account.
            </p>
          )}
          <label className="flex flex-col gap-1 font-mono text-xs text-ink">
            <span className="text-ink-muted">
              Type the username exactly to confirm:{" "}
              <strong className="text-ink">{user.username}</strong>
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              autoComplete="off"
              data-testid="admin-users-delete-confirm-username"
              className="rounded border border-rule bg-field px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          {error ? (
            <p
              data-testid="admin-users-delete-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Deleting…" : "Delete everywhere"}
      confirmDisabled={!confirmed || mut.isPending}
      danger
      onConfirm={() => {
        setError(null);
        if (!confirmed) {
          setError("Type the username exactly before deleting this account.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-delete-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// G3 — User detail drawer (modal-style)
// ---------------------------------------------------------------------------

function UserDetailModal({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}) {
  // Re-review P2 fix: share the same query options as the inline
  // G6 expander so opening the drawer after the expander reuses
  // the cached payload instead of triggering an immediate refetch.
  const query = useAdminUserDetail(user.id);

  return (
    <ConfirmDialog
      title={`Details — ${user.display_name || user.username}`}
      confirmLabel="Close"
      message={
        query.isLoading ? (
          <p className="text-ink-muted font-mono text-xs">Loading…</p>
        ) : query.error ? (
          <p
            data-testid="admin-users-detail-error"
            className="text-destructive font-mono text-xs"
          >
            {formatErr(query.error)}
          </p>
        ) : query.data ? (
          <div className="flex flex-col gap-3 text-xs">
            <section data-testid="admin-users-detail-profile">
              <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted mb-1">
                Profile
              </h4>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 font-mono text-[0.65rem]">
                <dt className="text-ink-muted">Username</dt>
                <dd className="text-ink">@{query.data.user.username}</dd>
                <dt className="text-ink-muted">Display name</dt>
                <dd className="text-ink">
                  {query.data.user.display_name || "—"}
                </dd>
                <dt className="text-ink-muted">Roles</dt>
                <dd className="text-ink">{query.data.user.role}</dd>
                <dt className="text-ink-muted">Status</dt>
                <dd className={query.data.user.enabled ? "text-good" : "text-ink-muted"}>
                  {query.data.user.enabled ? "Enabled" : "Disabled"}
                </dd>
                <dt className="text-ink-muted">Secure sign-in</dt>
                <dd className="text-ink">
                  {authMigrationText(query.data.user)}
                </dd>
                <dt className="text-ink-muted">Created</dt>
                <dd className="text-ink">{query.data.user.created_at || "—"}</dd>
                <dt className="text-ink-muted">Last team</dt>
                <dd className="text-ink">{query.data.user.last_team_id || "—"}</dd>
              </dl>
            </section>

            <section data-testid="admin-users-detail-memberships">
              <header className="flex items-baseline justify-between mb-1">
                <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Team memberships ({query.data.memberships.length})
                </h4>
                <span className="text-[0.6rem] font-mono text-ink-muted">
                  Managed from the account row
                </span>
              </header>
              {query.data.memberships.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  No memberships.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {query.data.memberships.map((m) => (
                    <li
                      key={m.id}
                      data-testid="admin-users-detail-membership-row"
                      className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1 font-mono text-[0.65rem]"
                    >
                      <div className="min-w-0 truncate">
                        <span className="text-ink">{m.team_name}</span>{" "}
                        <span className="text-ink-muted">· {m.team_slug}</span>{" "}
                        <span className="text-ink-muted">· {m.role}</span>
                      </div>
                      <span
                        data-team-id={m.team_id}
                        className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
                      >
                        Assigned
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="admin-users-detail-players">
              <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted mb-1">
                Linked players ({query.data.linked_players.length})
              </h4>
              {query.data.linked_players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  Not linked to any players.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {query.data.linked_players.map((p) => (
                    <li
                      key={`${p.player_id}-${p.relationship}`}
                      data-testid="admin-users-detail-player-row"
                      className="rounded border border-rule/60 bg-elevated px-2 py-1 font-mono text-[0.65rem]"
                    >
                      <span className="text-ink">{p.player_name}</span>{" "}
                      {p.jersey_number ? (
                        <span className="text-ink-muted">#{p.jersey_number}</span>
                      ) : null}{" "}
                      <span className="text-ink-muted">· {p.team_name}</span>{" "}
                      <span className="text-ink-muted">· {p.relationship}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null
      }
      onConfirm={onClose}
      onClose={onClose}
      data-testid="admin-users-detail-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// Row-level team assignment
// ---------------------------------------------------------------------------

function AssignUserToTeamModal({
  user,
  onClose,
  onAssigned,
}: {
  user: AdminUser;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const toast = useToast();
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    staleTime: 60_000,
  });
  const teams = useMemo(
    () =>
      [...(teamsQuery.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [teamsQuery.data],
  );
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState("coach");
  const [error, setError] = useState<string | null>(null);

  const selectedTeam = teams.find((team) => team.id === teamId);
  const teamOptions = teams.map((team: AdminTeamRow) => ({
    value: team.id,
    label: team.name,
  }));

  const mut = useMutation({
    mutationFn: () =>
      grantAdminTeamMembership(teamId, {
        user_id: user.id,
        role,
      }),
    onSuccess: () => {
      toast.success("Team assigned");
      onAssigned();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't assign team", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Assign to team"
      message={
        <div className="flex flex-col gap-3 text-xs">
          <p className="text-ink-muted">
            Add <strong className="text-ink">{user.display_name || user.username}</strong>{" "}
            to a team. The account row will show the assignment status after
            save.
          </p>
          {teamsQuery.isLoading ? (
            <p className="font-mono text-ink-muted">Loading teams…</p>
          ) : teamsQuery.error ? (
            <p className="font-mono text-destructive">Failed to load teams.</p>
          ) : teams.length === 0 ? (
            <p className="font-mono text-ink-muted">No teams yet.</p>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Team
                </span>
                <TkticSelect
                  value={teamId}
                  onChange={setTeamId}
                  options={teamOptions}
                  placeholder="Pick a team"
                  ariaLabel="Team"
                  size="sm"
                  data-testid="admin-users-assign-team"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Role
                </span>
                <TkticSelect
                  value={role}
                  onChange={setRole}
                  options={TEAM_ROLES}
                  ariaLabel="Role"
                  size="sm"
                  data-testid="admin-users-assign-role"
                />
              </label>
              {selectedTeam ? (
                <p className="rounded border border-rule bg-elevated px-2 py-1 font-mono text-[0.6rem] text-ink-muted">
                  {selectedTeam.slug} · {selectedTeam.game_format}
                </p>
              ) : null}
            </>
          )}
          {error ? (
            <p
              data-testid="admin-users-assign-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Assigning…" : "Assign team"}
      onConfirm={() => {
        setError(null);
        if (!teamId) {
          setError("Pick a team.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-assign-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function splitRoleString(role: string | undefined): string[] {
  if (!role) return [];
  return role
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function compareAdminUsers(a: AdminUser, b: AdminUser): number {
  const roleDelta = roleRank(a) - roleRank(b);
  if (roleDelta !== 0) return roleDelta;
  const aLabel = (a.display_name || a.username).toLowerCase();
  const bLabel = (b.display_name || b.username).toLowerCase();
  const labelDelta = aLabel.localeCompare(bLabel);
  if (labelDelta !== 0) return labelDelta;
  return a.username.localeCompare(b.username);
}

function roleRank(user: AdminUser): number {
  const roles = user.roles ?? splitRoleString(user.role);
  if (roles.includes("admin")) return 0;
  if (roles.includes("uploader")) return 1;
  return 2;
}

function authMigrationText(user: AdminUser): string {
  const migration = user.auth_migration;
  if (!migration?.kratos_linked) return "Needs secure sign-in link";
  return migration.kratos_email_verified ? "Linked and verified" : "Linked; email unverified";
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail)) {
      const messages = err.detail
        .map((entry) => {
          if (!entry || typeof entry !== "object") return null;
          const record = entry as Record<string, unknown>;
          const msg = typeof record.msg === "string" ? record.msg : null;
          const loc = Array.isArray(record.loc)
            ? record.loc
                .map((part) => String(part))
                .filter((part) => part !== "body")
                .join(".")
            : "";
          if (!msg) return null;
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter((msg): msg is string => !!msg);
      if (messages.length > 0) return messages.join("; ");
    }
    return `HTTP ${err.status}`;
  }
  return "Request failed";
}
